import React, { useCallback } from 'react'
import { ChatChannelInstance, ChatLocalMesage, ChatMember, MESSAGE_URL_REGEX } from '@packages/chat'
import { MessageText as ReactMessageText } from 'stream-chat-react'
import { ChatMessageStyles } from '../styles'
import { MemberOptionsModal, UserDetailsModal } from '@/components'
import { Getstream } from '@/services'
import { UserResponse } from 'stream-chat'

export type MessageTextProps = {
  channel: ChatChannelInstance
  message?: ChatLocalMesage
  styles: ChatMessageStyles
}

function MemoizedMessageText({ styles, message, channel }: MessageTextProps) {
  if (!message?.text) return null

  return (
    <div css={[styles.messageText]}>
      {/^[\s\-_*]+$/.test(message.text) ? (
        <div style={{ whiteSpace: 'pre-wrap' }}>{message.text}</div>
      ) : (
        <Content message={message} styles={styles} channel={channel} />
      )}
    </div>
  )
}

type ContentProps = { message: ChatLocalMesage; styles: ChatMessageStyles; channel: ChatChannelInstance }

const Content = ({ message, styles, channel }: ContentProps) => {
  const mentionedUsers = message.mentioned_users || []
  const { isGroup, isAdmin } = Getstream.Chat.useMyMembership(channel)

  const handleMentionedUserPress = useCallback((user: UserResponse) => {
    if (!isGroup) {
      UserDetailsModal.open({ user })
      return
    }

    const memberToShow = ({
      user: user,
      user_id: user?.id,
    } as ChatMember)

    MemberOptionsModal.open({
      member: memberToShow,
      isAdmin,
      channel,
    })
  }, [isGroup, isAdmin])

  return (
    <ReactMessageText
      renderText={(text) => (
        <p style={{ margin: 0 }}>
          {parseText(text, mentionedUsers, styles, handleMentionedUserPress)}
        </p>
      )}
    />
  )
}

function parseText(
  text: string,
  mentionedUsers: UserResponse[] = [],
  styles: ChatMessageStyles,
  onMentionedUserPress?: (user: UserResponse) => void): (string | React.ReactElement)[] {
  const parts: (string | React.ReactElement)[] = []
  let currentIndex = 0

  const linkRegex = new RegExp(MESSAGE_URL_REGEX.source, MESSAGE_URL_REGEX.flags)

  const matches: Array<{
    type: 'link' | 'mention'
    start: number
    end: number
    value: string
    user?: UserResponse
  }> = []

  let match
  while ((match = linkRegex.exec(text)) !== null) {
    matches.push({
      type: 'link',
      start: match.index,
      end: match.index + match[0].length,
      value: match[0],
    })
  }

  mentionedUsers.forEach((user) => {
    const userName = user.name || user.id
    const mentionPattern = `@${userName}`
    let searchIndex = 0

    while ((searchIndex = text.indexOf(mentionPattern, searchIndex)) !== -1) {
      const mentionEnd = searchIndex + mentionPattern.length

      const isInLink = matches.some(
        m => m.type === 'link' && searchIndex >= m.start && searchIndex < m.end,
      )

      if (!isInLink) {
        matches.push({
          type: 'mention',
          start: searchIndex,
          end: mentionEnd,
          value: mentionPattern,
          user,
        })
      }

      searchIndex = mentionEnd
    }
  })

  matches.sort((a, b) => a.start - b.start)

  const filteredMatches = []
  let lastEnd = -1
  for (const match of matches) {
    if (match.start >= lastEnd) {
      filteredMatches.push(match)
      lastEnd = match.end
    }
  }

  filteredMatches.forEach((match, index) => {
    if (currentIndex < match.start) {
      const textBefore = text.slice(currentIndex, match.start)
      parts.push(...splitTextWithNewlines(textBefore))
    }

    if (match.type === 'link') {
      const href = match.value.startsWith('http') ? match.value : `https://${match.value}`
      parts.push(
        <a
          key={`link-${index}`}
          className='str-chat__message-url-link'
          href={href}
          target='_blank'
          rel='noopener noreferrer'
        >
          {match.value}
        </a>,
      )
    } else if (match.type === 'mention') {
      parts.push(
        <span
          key={`mention-${index}`}
          css={styles.mention}
          onClick={() => onMentionedUserPress(match.user)}
        >
          {match.value}
        </span>,
      )
    }

    currentIndex = match.end
  })

  if (currentIndex < text.length) {
    const textAfter = text.slice(currentIndex)
    parts.push(...splitTextWithNewlines(textAfter))
  }

  return parts.length > 0 ? parts : [text]
}

function splitTextWithNewlines(text: string): (string | React.ReactElement)[] {
  const lines = text.split('\n')
  const result: (string | React.ReactElement)[] = []

  lines.forEach((line, i) => {
    if (i > 0) {
      result.push(<br key={`br-${i}`} />)
    }
    if (line) {
      result.push(line)
    }
  })

  return result
}

export const MessageText = React.memo(MemoizedMessageText, (prevProps, nextProps) => {
  if (prevProps.message?.text !== nextProps.message?.text) {
    return false
  }

  if (prevProps.message?.mentioned_users?.length !== nextProps.message?.mentioned_users?.length) {
    return false
  }

  return true
})
